import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Meteor } from 'meteor/meteor';
import { useTracker } from 'meteor/react-meteor-data';
import { useFind, useUser } from '/imports/hooks';
import { defer, firstBy, formatBytes, formatDate, formatDateTime, randomString } from '/imports/utils';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { toastOnError } from '/imports/ui/utils';
import { Icon } from '/imports/ui/Icon';
import { LoadingFill } from '/imports/ui/LoadingFill';
import { TextArea } from '/imports/ui/TextArea';
import { FileInput } from '/imports/ui/FileInput';
import { Publications } from '/imports/publications';
import { FileMeta } from '/imports/types';
import { fileHref } from '/imports/api/files';
import { Conversation, ConversationCandidate, modelClass as ConversationClass, getConversationForOrg, addConversationParticipant, listConversationCandidates, removeConversationParticipant } from '/imports/api/conversations';
import { Input } from '/imports/ui/Input';
import {
  ConversationParticipant, MESSAGE_EDIT_WINDOW_MS, Message, MessageReceipts, Messages, conversationParticipants, editMessage,
  markConversationMessagesRead, modelClass as messageModelClass, retractMessage,
} from '/imports/api/messages';

const ConversationFeedStyled = styled.div`
  position: fixed;
  z-index: 3;
  bottom: -100%;
  left: 0;
  width: 100%;
  height: calc(100% - 8.25rem);
  border-top: 2px solid ${css.black};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: ${css.white};
  transition: bottom 0.5s ease;
  ${css.desktopMedia} {
    left: auto;
    right: 0;
    width: 35rem;
    height: 50rem;
    border-left: 2px solid ${css.black};
  }
  &.transition {
    display: flex;
    bottom: 3.75rem;
    ${css.desktopMedia} {
      bottom: 0;
      height: calc(100% - 8.25rem + 4.75rem);
    }
  }
  .close {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    cursor: pointer;
    z-index: 111;
  }
  .header {
    text-align: center;
    padding: 2rem 0 1rem 0;
    .title {
      font-weight: bold;
    }
    .project-link {
      margin-top: 0.25rem;
      font-size: 0.8rem;
    }
    .project-link .link-btn { background: none; border: none; color: ${css.primaryColor}; cursor: pointer; padding: 0; font-size: inherit; }
    .participants {
      margin-top: 0.35rem;
      font-size: 0.8rem;
      color: ${css.grey3};
      display: flex;
      align-items: baseline;
      justify-content: center;
      gap: 0.4rem;
      flex-wrap: wrap;
      padding: 0 1rem;
      .names { overflow-wrap: anywhere; }
      .chip {
        display: inline-flex; align-items: center; gap: 0.25rem;
        background: ${css.grey8}; border: 1px solid ${css.grey6}; border-radius: 1rem;
        padding: 0.05rem 0.3rem 0.05rem 0.55rem;
      }
      .chip .who { color: ${css.grey4}; }
      .chip-remove { background: none; border: none; cursor: pointer; color: ${css.grey4}; font-size: 0.7rem; padding: 0 0.15rem; }
      .chip-remove:hover { color: ${css.red2}; }
      /* The picker opens a dropdown; it needs the row's full width, not a shrunken flex slot. */
      .picker { flex: 1 0 100%; text-align: left; position: relative; }
      .picker-menu {
        position: absolute; z-index: 5; left: 0; right: 0; margin-top: 0.2rem;
        background: ${css.white}; border: ${css.inputBorder}; border-radius: ${css.inputBorderRadius};
        max-height: 14rem; overflow: auto; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
      }
      .picker-menu .opt { padding: 0.4rem 0.75rem; cursor: pointer; }
      .picker-menu .opt:hover { background: ${css.grey8}; }
    }
  }
  .messages {
    height: 100%;
    overflow: auto;
  }
  /* Day and "new" separators: a hairline with the label sitting on it. */
  .separator {
    display: flex; align-items: center; gap: 0.75rem;
    margin: 0.75rem 1rem; font-size: 0.75rem; color: ${css.grey4};
    &::before, &::after { content: ''; flex: 1; border-top: 1px solid ${css.grey6}; }
    &.new { color: ${css.primaryColor}; font-weight: bold; }
    &.new::before, &.new::after { border-color: ${css.primaryColor}; }
  }
  .form {
    position: relative;
    width: 100%;
    padding: 1rem;
    box-sizing: border-box;
    textarea {
      width: 100%;
      height: 3rem;
    }
    .composer-row { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.3rem; font-size: 0.75rem; color: ${css.grey4}; }
    .composer-row .hint { flex: 1; }
    /* The attach control is FileInput's drop target reshaped into a small pill. */
    .attach { display: inline-flex; width: max-content; }
    .attach .target { border: none; background: ${css.grey7}; color: ${css.grey3}; border-radius: 1rem; padding: 0.2rem 0.6rem; font-size: 0.75rem; }
    .attach .target:hover { background: ${css.grey6}; color: ${css.grey1}; }
    .attach .target .icon { margin: 0; }
    .attach .target .icon svg { width: 0.85rem; height: 0.85rem; min-width: 0.85rem; }
    .pending-file {
      display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.75rem;
      background: ${css.grey8}; border: 1px solid ${css.grey6}; border-radius: 1rem; padding: 0.05rem 0.3rem 0.05rem 0.55rem;
    }
    .pending-file button { background: none; border: none; cursor: pointer; color: ${css.grey4}; font-size: 0.7rem; }
    .mention-menu {
      position: absolute; left: 1rem; right: 1rem; bottom: calc(100% - 0.5rem);
      background: ${css.white}; border: ${css.inputBorder}; border-radius: ${css.inputBorderRadius};
      max-height: 12rem; overflow: auto; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15); z-index: 6;
      .opt { padding: 0.4rem 0.75rem; cursor: pointer; font-size: 0.85rem; }
      .opt:hover { background: ${css.grey8}; }
      .opt .who { color: ${css.grey4}; font-size: 0.75rem; margin-left: 0.4rem; }
    }
    .new-messages {
      position: absolute;
      left: 0;
      top: -0.5rem;
      text-align: center;
      width: 100%;
      span {
        display: inline-block;
        cursor: pointer;
        background: ${css.primaryColor};
        color: ${css.white};
        font-size: 0.8rem;
        padding: 0.25rem 0.5rem;
        border-radius: 0.5rem;
        opacity: 0.8;
        animation: vbounce 0.5s;
        animation-direction: alternate; 
        animation-iteration-count: infinite; 
      }
    }
  }

  @keyframes vbounce { 
      from { transform: translate3d(0, 0, 0);} 
      to { transform: translate3d(0, 5px, 0);} 
  }
`;

type ConversationFeedProps = {
  conversationId?: string;
  orgId?: string;
  className?: string;
  showProjectLink?: boolean;  // a way to the project, for readers who are not already on its page
  onClose?: () => void;
};

const URL_PATTERN = /(https?:\/\/[^\s<>"']+)/g;
const IS_URL = /^https?:\/\//;
// A mention being typed: an @ at a word start, run to the caret, which is always the end of the text.
const MENTION_TYPING = /(^|\s)@([^\s@]{0,40})$/;

/** Today / Yesterday / the date, for the separators between days. */
const dayLabel = (d: Date) => {
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  const now = new Date();
  if (sameDay(d, now)) return strings.Today();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (sameDay(d, yesterday)) return strings.Yesterday();
  return formatDate(d);
};

/** Text with its URLs made clickable. Plain text otherwise — nothing else is interpreted. */
const linkify = (text: string) => text.split(URL_PATTERN).map((part, i) => (
  IS_URL.test(part)
    ? <a key={i} href={part} target="_blank" rel="noreferrer">{part}</a>
    : <React.Fragment key={i}>{part}</React.Fragment>
));

type Row = { kind: 'message', at: Date, message: Message };

/**
 * Slide-up conversation panel: loads a conversation, streams messages (and, on a project chat, the
 * project's key events between them), and sends replies — with attachments and @mentions.
 */
export const ConversationFeed = (props: ConversationFeedProps = {}) => {
  const { conversationId, orgId, className, showProjectLink, onClose } = props;
  const user = useUser();
  const userId = user?._id;
  const [loadingConversation, setLoadingConversation] = useState(true);
  const loading = loadingConversation && !user;
  const [textareaId] = useState(`t${randomString()}`);
  const [messageDivId] = useState(`m${randomString()}`);
  const [conversation, setConversation] = useState<Conversation>();
  // Everyone in the thread, as the server resolves them (stakeholders with accounts + the explicit lists):
  // the header, the @mention menu and "seen by" all read from this.
  const [participants, setParticipants] = useState<ConversationParticipant[]>([]);
  // The super user's management of the EXPLICIT lists — the people project access does not cover.
  const [candidates, setCandidates] = useState<ConversationCandidate[]>([]);
  const [addingParticipant, setAddingParticipant] = useState(false);
  const [participantSearch, setParticipantSearch] = useState('');
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  // What was unread when the thread opened: the "new" separator sits before the first of these.
  const unreadAtOpen = useRef<Set<string>>(new Set());
  useEffect(() => { loadConversation(); }, []);
  useEffect(() => {
    if (user?.isSuperUser) listConversationCandidates.call(undefined).then(setCandidates).catch(() => undefined);
  }, [user?.isSuperUser]);
  const isExplicit = (p: ConversationParticipant) =>
    !!((p.accountId && conversation?.accountIds?.includes(p.accountId)) || (p.userId && conversation?.userIds?.includes(p.userId)));
  const loadParticipants = async () => {
    if (conversation?._id) setParticipants(await conversationParticipants.call(conversation._id).catch(() => []));
  };
  useEffect(() => { loadParticipants(); }, [conversation?._id, conversation?.accountIds?.length, conversation?.userIds?.length]);
  /** Adds an account or super user to this conversation, then reloads so the list reflects it. */
  const addParticipant = toastOnError(async (candidate: ConversationCandidate) => {
    if (!conversation?._id) return;
    await addConversationParticipant.call({
      conversationId: conversation._id,
      ...(candidate.kind === 'account' ? { accountId: candidate._id } : { userId: candidate._id }),
    });
    setAddingParticipant(false);
    setParticipantSearch('');
    await loadConversation();
  });
  /** Removes a participant; only un-does an explicit Add — project access is untouched. */
  const removeParticipant = toastOnError(async (p: ConversationParticipant) => {
    if (!conversation?._id) return;
    await removeConversationParticipant.call({
      conversationId: conversation._id,
      ...(p.accountId && conversation.accountIds?.includes(p.accountId) ? { accountId: p.accountId } : { userId: p.userId }),
    });
    await loadConversation();
  });
  const participantQuery = participantSearch.trim().toLowerCase();
  const participantMatches = participantQuery
    ? candidates
      .filter((c) => !participants.some((p) => (c.kind === 'account' ? p.accountId === c._id : p.userId === c._id)))
      .filter((c) => `${c.name} ${c.email || ''} ${c.org || ''}`.toLowerCase().includes(participantQuery)).slice(0, 8)
    : [];
  /** Fetches the conversation by id or by org. */
  const loadConversation = toastOnError(async () => {
    if (conversationId) setConversation(await ConversationClass.getMethod.call(conversationId));
    else if (orgId) setConversation(await getConversationForOrg.call(orgId));
    setLoadingConversation(false);
  });
  const [messageSubscription, setMessageSubscription] = useState<Meteor.SubscriptionHandle>();
  useEffect(() => {
    if (conversation?._id && userId) {
      unreadAtOpen.current = new Set(MessageReceipts.find({ conversationId: conversation._id, userId, readAt: { $exists: false } }).map((r) => r.messageId));
    }
    markAsRead();
    subscribeToMessages();
  }, [conversation?._id]);
  // Read receipts (for "seen by") — a harmless no-op for a reader the server does not allow them for.
  useTracker(() => {
    if (!conversation?._id) return;
    Meteor.subscribe(Publications.conversationReadReceipts, { conversationId: conversation._id });
  }, [conversation?._id]);
  /** Subscribes to the conversation's messages and flips messagesLoaded once ready. */
  const subscribeToMessages = () => {
    const conversationId = conversation?._id;
    const messageSubscription = conversationId && Meteor.subscribe(Publications.conversationMessages, { conversationId, limit }) || undefined;
    messageSubscription && setMessageSubscription(messageSubscription);
    const interval = setInterval(() => {
      if (messageSubscription?.ready()) {
        setMessagesLoaded(true);
        clearInterval(interval);
      }
    }, 100);
  };
  /** Marks the conversation's messages as read. */
  const markAsRead = async () => {
    conversationId && await markConversationMessagesRead.call(conversationId);
  };
  const [limit, setLimit] = useState(10);
  useEffect(() => {
    subscribeToMessages();
  }, [limit]);
  /** On scroll to top, raises the message limit and keeps the viewport pinned to the prior top message. */
  const handleScrollMessages = () => {
    const messageDiv = document.getElementById(messageDivId) as HTMLDivElement;
    if (!messageDiv) return;
    if (messageDiv.scrollTop === 0) {
      const topMessage = sortedMessages[0];
      const topMessageDiv = document.getElementById(`b${topMessage?._id}`);
      setLimit(limit + 10);
      let iters = 0;
      let lastScrollHeight = messageDiv.scrollHeight;
      const interval = setInterval(() => {
        topMessageDiv?.scrollIntoView();
        const scrollHeight = messageDiv.scrollHeight;
        if (iters > 50 && lastScrollHeight === scrollHeight || iters > 1000) clearInterval(interval);
        lastScrollHeight = scrollHeight;
        iters += 1;
      }, 1);
    }
  };
  const [content, setContent] = useState<string>();
  const [pendingFile, setPendingFile] = useState<FileMeta>();
  // Mentions picked from the menu, by name: at send time only the ones still present as "@Name" count.
  const [mentions, setMentions] = useState<{ name: string, userId: string }[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string>();
  const [transition, setTransition] = useState(false);
  useEffect(() => { setTransition(true); }, []);
  /** Plays the slide-out transition, then invokes onClose. */
  const close = () => {
    setTransition(false);
    setTimeout(() => onClose?.(), 500);
  };
  /** Puts text in the composer — state and the element both, since the element is not fully controlled. */
  const setComposer = (text: string) => {
    setContent(text);
    const textarea = document.getElementById(textareaId) as HTMLTextAreaElement;
    if (textarea) textarea.value = text;
  };
  const onComposerChange = (text: string) => {
    setContent(text);
    const typing = text.match(MENTION_TYPING);
    setMentionQuery(typing ? typing[2].toLowerCase() : undefined);
  };
  const mentionMatches = mentionQuery !== undefined
    ? participants.filter((p) => p.userId && p.userId !== userId && p.name.toLowerCase().includes(mentionQuery)).slice(0, 6)
    : [];
  /** Completes the mention being typed with the chosen person. */
  const pickMention = (p: ConversationParticipant) => {
    if (!p.userId) return;
    const text = (content || '').replace(MENTION_TYPING, `$1@${p.name} `);
    setComposer(text);
    setMentions([...mentions.filter((m) => m.userId !== p.userId), { name: p.name, userId: p.userId }]);
    setMentionQuery(undefined);
    (document.getElementById(textareaId) as HTMLTextAreaElement | null)?.focus();
  };
  const [sending, setSending] = useState(false);
  /** Sends the current textarea content (and any attachment) as a new message. */
  const sendMessage = toastOnError(async () => {
    const text = (content || '').trim();
    if (!text && !pendingFile) return;
    setSending(true);
    try {
      const conversationId = conversation?._id;
      const mentionUserIds = mentions.filter((m) => text.includes(`@${m.name}`)).map((m) => m.userId);
      conversationId && await messageModelClass.createMethod.call({
        conversationId, content: text || pendingFile?.name || '', file: pendingFile, mentionUserIds: mentionUserIds.length ? mentionUserIds : undefined,
      });
      clearContent();
    }
    finally {
      setSending(false);
    }
  });
  /**
   * Sends on Enter (without Shift); Enter with a mention menu open picks its first entry instead.
   * @param e - keyboard event from the textarea
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.nativeEvent.preventDefault();
      if (mentionMatches.length) pickMention(mentionMatches[0]);
      else sendMessage();
    }
    if (e.key === 'Escape' && mentionQuery !== undefined) setMentionQuery(undefined);
  }, [content, pendingFile, mentions, mentionMatches]);
  /** Clears the message content state and the textarea element. */
  const clearContent = () => {
    setComposer('');
    setContent(undefined);
    setPendingFile(undefined);
    setMentions([]);
    setMentionQuery(undefined);
  };
  const messages = useFind(() => Messages.find({ conversationId: conversation?._id }), [conversation?._id, messageSubscription?.ready()]);
  const sortedMessages = useMemo(() => messages.sort(firstBy('createdAt', { direction: 'asc' })), [messages]);
  const receipts = useFind(() => MessageReceipts.find({ conversationId: conversation?._id }), [conversation?._id]);
  const rows = useMemo<Row[]>(() => sortedMessages.map((m) => ({ kind: 'message' as const, at: m.createdAt || new Date(0), message: m })), [sortedMessages]);
  const nameOf = (id?: string) => participants.find((p) => p.userId === id)?.name;
  const mostRecentMessage = useRef(sortedMessages[sortedMessages.length - 1]);
  const [showNewMessagesBubble, setShowNewMessagesBubble] = useState(false);
  const [initialScroll, setInitialScroll] = useState(true);
  useEffect(() => {
    const _mostRecentMessage = sortedMessages[sortedMessages.length - 1];
    const _createdAt = _mostRecentMessage?.createdAt?.getTime() || 1;
    const createdAt = mostRecentMessage.current?.createdAt?.getTime() || 0;
    if (_createdAt > createdAt) {
      mostRecentMessage.current = _mostRecentMessage;
      scrollToBottom();
    }
    markAsRead();
  }, [sortedMessages]);
  useEffect(() => {
    messagesLoaded && defer(() => scrollToBottom(true, 'instant'));
  }, [messagesLoaded]);
  /**
   * Scrolls the message list to the bottom, or shows the new-messages bubble if the user scrolled up.
   * @param force - scroll even when the user is not near the bottom
   * @param behavior - scroll behavior
   */
  const scrollToBottom = (force = false, behavior: ScrollBehavior = 'smooth') => {
    const messageDiv = document.getElementById(messageDivId) as HTMLDivElement;
    if (!messageDiv) return;
    force = force || initialScroll || !messageSubscription?.ready();
    if (force || messageDiv.scrollHeight - messageDiv.clientHeight - messageDiv.scrollTop <= 133) {
      setShowNewMessagesBubble(false);
      setInitialScroll(false);
      messageDiv?.scrollTo({ top: messageDiv.scrollHeight, behavior });
    }
    else {
      setShowNewMessagesBubble(true);
    }
  };
  // Separators are decided while rendering: a day label whenever the date changes, and "new" once, before
  // the first message that was unread when the thread opened.
  let lastDay = '';
  let newMarked = false;
  return (
    <ConversationFeedStyled className={`conversation-feed${transition ? ' transition' : ''}${className || ''}`}>
      {
        loading ?
          <LoadingFill />
          :
          null
      }
      {
        conversation ?
          <>
            <div className="header">
              <div className="title">{conversation.name}</div>
              {
                conversation.projectManagementProjectId && showProjectLink &&
                <div className="project-link">
                  <Link to={`/project-management/projects/${conversation.projectManagementProjectId}`}>{strings.OpenProject()}</Link>
                </div>
              }
              <div className="participants">
                <span className="names">{strings.Participants()}:</span>
                {
                  participants.length ?
                    participants.map((p) => (
                      <span key={p.userId || p.accountId} className="chip">
                        {p.name}{p.label ? <span className="who"> · {p.label}</span> : null}
                        {user?.isSuperUser && isExplicit(p) && <button className="chip-remove" onClick={() => removeParticipant(p)}>✕</button>}
                      </span>
                    ))
                    : <span className="names">—</span>
                }
                {user?.isSuperUser && <button className="link-btn" onClick={() => setAddingParticipant((v) => !v)}>{strings.Add()}</button>}
                {
                  user?.isSuperUser && addingParticipant &&
                  <div className="picker">
                    <Input value={participantSearch} placeholder={strings.Search()} onChange={(v) => setParticipantSearch(v || '')} />
                    {
                      participantMatches.length ?
                        <div className="picker-menu">
                          {
                            participantMatches.map((c) => (
                              <div key={`${c.kind}_${c._id}`} className="opt" onClick={() => addParticipant(c)}>
                                {c.name}{c.email ? ` · ${c.email}` : ''}{c.org ? ` · ${c.org}` : ''}{c.kind === 'superUser' ? ` · ${strings.SuperUser()}` : ''}
                              </div>
                            ))
                          }
                        </div>
                        : null
                    }
                  </div>
                }
              </div>
            </div>
            <div id={messageDivId} className="messages" onScroll={() => handleScrollMessages()}>
              {
                rows.map((row) => {
                  const out: React.ReactNode[] = [];
                  const day = row.at.toDateString();
                  if (day !== lastDay) {
                    lastDay = day;
                    out.push(<div key={`day-${day}`} className="separator">{dayLabel(row.at)}</div>);
                  }
                  const m = row.message;
                  if (!newMarked && m.senderUserId !== userId && unreadAtOpen.current.has(m._id)) {
                    newMarked = true;
                    out.push(<div key="new" className="separator new">{strings.New()}</div>);
                  }
                  out.push(
                    <MessageBubble
                      id={`b${m._id}`} key={m._id} message={m} isSender={m.senderUserId === userId}
                      seenBy={m.senderUserId === userId ? receipts.filter((r) => r.messageId === m._id && r.readAt && r.userId !== userId).map((r) => nameOf(r.userId)).filter(Boolean) as string[] : []}
                    />,
                  );
                  return out;
                })
              }
            </div>
            <div className="form">
              {
                mentionMatches.length ?
                  <div className="mention-menu">
                    {mentionMatches.map((p) => (
                      <div key={p.userId} className="opt" onMouseDown={(e) => e.preventDefault()} onClick={() => pickMention(p)}>
                        {p.name}{p.label ? <span className="who">{p.label}</span> : null}
                      </div>
                    ))}
                  </div>
                  : null
              }
              <TextArea id={textareaId} value={content} onKeyDown={(e) => handleKeyDown(e)} onChange={(text) => onComposerChange(text)} />
              <SendButton sending={sending} onSend={() => sendMessage()} />
              <div className="composer-row">
                <span className="hint">{strings.MessageComposerHint()}</span>
                {
                  pendingFile ?
                    <span className="pending-file">
                      <Icon icon="attachFile" />{pendingFile.name} · {formatBytes(pendingFile.size)}
                      <button onClick={() => setPendingFile(undefined)}>✕</button>
                    </span>
                    : <FileInput className="attach" placeholder={strings.AttachFile()} onChange={(file) => setPendingFile(file)} />
                }
              </div>
              {
                showNewMessagesBubble ?
                  <div className="new-messages"><span onClick={() => scrollToBottom(true)}>{strings.NewMessages()} ⮟</span></div>
                  :
                  null
              }
            </div>
          </>
          :
          null
      }
      <Icon className="close" icon="close" onClick={() => close()} />
    </ConversationFeedStyled>
  );
};

const MessageBubbleStyled = styled.div`
  margin-bottom: 1rem;
  padding: 1rem;
  box-sizing: border-box;
  position: relative;
  .sender {
    font-size: 0.75rem;
    color: ${css.grey4};
  }
  .content {
    padding: 0.75rem;
    border-radius: 1rem;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    a { color: inherit; text-decoration: underline; }
    &.deleted { font-style: italic; opacity: 0.7; }
  }
  .attachment {
    display: inline-flex; align-items: center; gap: 0.3rem; margin-top: 0.4rem; font-size: 0.85rem;
    a { color: inherit; }
  }
  .timestamp {
    font-size: 0.75rem;
    color: ${css.grey4};
  }
  .seen { font-size: 0.7rem; color: ${css.grey5}; }
  .actions {
    display: none; font-size: 0.72rem; margin-top: 0.15rem;
    .link-btn { background: none; border: none; color: ${css.grey4}; cursor: pointer; padding: 0 0.2rem; font-size: inherit; }
    .link-btn:hover { color: ${css.primaryColor}; }
  }
  &:hover .actions { display: block; }
  .edit-box {
    textarea { width: 100%; height: 4rem; }
    .edit-actions { display: flex; gap: 0.5rem; justify-content: flex-end; margin-top: 0.3rem; font-size: 0.8rem; }
    .edit-actions button { background: none; border: none; color: ${css.primaryColor}; cursor: pointer; }
  }
  &.sent {
    .sender, .timestamp, .seen, .actions { text-align: right; margin-right: 1rem; }
    .content {
      background: ${css.skyBlue};
      color: ${css.white};
      border-top-right-radius: 0;
    }
    .attachment { float: right; margin-right: 1rem; }
  }
  &.received {
    .sender, .timestamp, .actions { margin-left: 1rem; }
    .content {
      background: ${css.grey7};
      border-top-left-radius: 0;
    }
    .attachment { margin-left: 1rem; }
  }
`;

interface MessageBubbleProps {
  id: string;
  message: Message;
  isSender: boolean;
  seenBy: string[];
}

/**
 * Renders a single message bubble, styled as sent or received: the sender and who they are, the text with
 * its links live, an attachment if any, when it was sent (and edited), who has seen it, and — for the
 * sender's own recent messages — edit and delete.
 */
const MessageBubble = (props: MessageBubbleProps) => {
  const { id, message, isSender, seenBy } = props;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.content);
  // The person, then who they are to the project (an older message without a label falls back to its org).
  const label = message.senderLabel || message.senderOrgName;
  const canChange = isSender && !message.deletedAt && !!message.createdAt && Date.now() - message.createdAt.getTime() < MESSAGE_EDIT_WINDOW_MS;
  const save = toastOnError(async () => {
    await editMessage.call({ _id: message._id, content: draft });
    setEditing(false);
  });
  const retract = toastOnError(async () => { await retractMessage.call(message._id); });
  return (
    <MessageBubbleStyled id={id} className={isSender ? 'message sent' : 'message received'}>
      <div className="sender">
        {isSender ? strings.You() : `${message.senderName}${label ? ` · ${label}` : ''}`}
      </div>
      {
        editing ?
          <div className="edit-box">
            <TextArea value={draft} onChange={(t) => setDraft(t)} />
            <div className="edit-actions">
              <button onClick={() => { setEditing(false); setDraft(message.content); }}>{strings.Cancel()}</button>
              <button onClick={() => save()}>{strings.Save()}</button>
            </div>
          </div>
          :
          <div className={`content${message.deletedAt ? ' deleted' : ''}`}>
            {message.deletedAt ? strings.MessageDeleted() : linkify(message.content)}
          </div>
      }
      {
        message.file && !message.deletedAt &&
        <div className="attachment">
          <Icon icon="attachFile" />
          <a href={fileHref(message.file)} target="_blank" rel="noreferrer">{message.file.name}</a>
          <span>· {formatBytes(message.file.size)}</span>
        </div>
      }
      <div className="timestamp">
        {message.createdAt && formatDateTime(message.createdAt) || null}{message.editedAt ? ` · ${strings.Edited()}` : ''}
      </div>
      {seenBy.length ? <div className="seen">{strings.SeenBy()} {seenBy.join(', ')}</div> : null}
      {
        canChange && !editing &&
        <div className="actions">
          <button className="link-btn" onClick={() => { setDraft(message.content); setEditing(true); }}>{strings.Edit()}</button>
          <button className="link-btn" onClick={() => retract()}>{strings.Delete()}</button>
        </div>
      }
    </MessageBubbleStyled>
  );
};

const SendButtonStyled = styled.span`
  position: absolute;
  /* Centred on the textarea, not on .form: the new-messages bubble is a sibling inside .form, so a
     50% offset would shift the button whenever that appeared. 1rem of form padding + half of the
     textarea's 3rem, pulled back by half its own height. */
  top: 2.5rem;
  transform: translateY(-50%);
  right: 2rem;
  transition: right 0.25s ease;
  cursor: pointer;
  /* flex, not the default block: as an inline context this span was 29px tall around a 24px glyph, the
     extra 5px being line-height space BELOW it — so centring the span left the icon sitting 2px high. */
  display: flex;
  align-items: center;
  svg {
    transition: width 0.25s ease;
    width: 1.5rem;
  }
  &.sending {
    svg {
      * {
        fill: ${css.grey5};
      }
    }
  }
  &:hover {
    right: 1.5rem;
    svg {
      width: 1.75rem;
    }
  }
`;

interface SendButtonProps {
  sending?: boolean;
  onSend?: () => void;
}

/**
 * Send-message button. The generic Material "send" glyph rather than the branded three-tone mark that
 * used to be inlined here — a send control reads faster when it looks like every other one.
 */
const SendButton = (props: SendButtonProps) => {
  const sending = props.sending;
  return (
    <SendButtonStyled onClick={() => props.onSend?.()} className={sending ? 'sending' : ''}>
      <Icon icon="send" fill={css.primaryColor} />
    </SendButtonStyled>
  );
};
